import React, { useState, useCallback, useEffect, useRef } from "react";
import trpc from "../utils/trpc";
import { Link } from "react-router-dom";
import { FillContainerLoader } from "./loader";
import useCompletedByItemId from "@/hooks/use-completed-by-item-id";
import useCurrentModuleItemId from "@/hooks/use-current-module-item-id";
import { buildItemLink } from "@/utils/canvas";
import { ChevronRightIcon, CheckIcon } from "@heroicons/react/24/outline";
import InlineError from "./inline-error";
interface ModulesSidebarProps {
  onNavigate?: () => void;
}

const ModulesSidebar: React.FC<ModulesSidebarProps> = ({ onNavigate }) => {
  // Track which modules are expanded; key is module id
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [initialized, setInitialized] = useState(false);
  const toggle = useCallback((id: string) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const {
    data: modules,
    isLoading,
    error,
  } = trpc.course.getModules.useQuery(undefined, {
    staleTime: Infinity,
  });

  const { completedByItemId, isProgressionSuccess } = useCompletedByItemId();

  const currentModuleItemId = useCurrentModuleItemId();

  // Manage expansion behaviour:
  // 1. On initial load: initialize all modules as collapsed
  // 2. If there's an active item, ensure its module is expanded
  // 3. When active item changes later, ensure its module is expanded
  useEffect(() => {
    if (!modules || modules.length === 0) return;
    if (!initialized) {
      setExpanded(prev => {
        if (Object.keys(prev).length > 0) return prev;
        const defaults: Record<string, boolean> = {};
        for (const m of modules) {
          defaults[m.id] = false;
        }
        return defaults;
      });
      setInitialized(true);
    }

    if (initialized && currentModuleItemId) {
      const targetModule = modules.find(m => {
        return m.subModules.some(sub => sub.items.some(it => String(it.id) === currentModuleItemId));
      });
      if (targetModule) {
        setExpanded(prev => {
          if (prev[targetModule.id]) return prev; // already expanded
          return { ...prev, [targetModule.id]: true };
        });
      }
    }
  }, [modules, currentModuleItemId, initialized]);

  // Auto-scroll for active item
  const activeItemRef = useRef<HTMLLIElement | null>(null);
  const lastScrolledIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!currentModuleItemId) return;
    if (lastScrolledIdRef.current === currentModuleItemId) return; // already scrolled to this id
    if (!activeItemRef.current) return;
    try {
      activeItemRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
      lastScrolledIdRef.current = currentModuleItemId;
    } catch (_) {
      // ignore
    }
  }, [currentModuleItemId]);

  if (isLoading) return <FillContainerLoader delayMs={0} />;
  if (error) return <InlineError message="We couldn't load the course modules. Please try refreshing the page." />;
  if (!modules || modules.length === 0) return <div className="text-sm">No modules available.</div>;

  return (
    <ul>
      {modules.map((module, index) => {
        const subModules = module.subModules;
        const isOpen = expanded[module.id] || false;
        const flatItems = (subModules ?? []).flatMap(s => s.items);
        const isLastModule = index === modules.length - 1;

        // Determine module-level completion: only consider items that have a completion requirement
        const requiredItems = flatItems.filter(it => Boolean(it.completion_requirement));
        const completedRequiredCount = requiredItems.filter(it => completedByItemId.has(String(it.id))).length;
        const totalRequiredCount = requiredItems.length;
        const showProgress = isProgressionSuccess && totalRequiredCount > 0;

        return (
          <li key={module.id} className="overflow-hidden first:rounded-t-xl last:rounded-b-xl">
            <button
              className={`flex w-full items-center justify-between border-gray px-3 py-2.5 transition-colors hover:bg-lac-leman-8/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lac-leman focus-visible:ring-offset-2 focus-visible:ring-offset-white ${
                isLastModule || isOpen ? "" : "border-b"
              }`}
              aria-label={`${isOpen ? "Collapse" : "Expand"} module ${module.name}`}
              aria-expanded={isOpen}
              onClick={() => toggle(module.id)}>
              <div className="flex items-center gap-2">
                {
                  <ChevronRightIcon
                    className={`h-4 min-w-4 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`}
                  />
                }

                <span className="text-left text-sm font-semibold md:text-base">{module.name}</span>
              </div>
              <span
                className={`ml-4 inline-flex items-center gap-2 rounded-full border border-gray bg-lac-leman-8/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] ${
                  showProgress ? "" : "opacity-0"
                }`}
                aria-hidden={!showProgress}>
                <span>
                  {completedRequiredCount}/{totalRequiredCount}
                </span>
              </span>
            </button>
            {isOpen && (
              <ul className={`border-b border-gray px-4 py-4 pb-6`}>
                {flatItems.length === 0 ? (
                  <li className="rounded-xl border border-gray px-3 py-3">
                    <span className="text-sm">No items available.</span>
                  </li>
                ) : null}
                {(subModules ?? []).map((sub, i) => (
                  <React.Fragment key={sub.id}>
                    {sub.title ? (
                      <li className={`px-2 ${i === 0 ? "" : "mt-3"}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-sm">{sub.title}</span>
                        </div>
                      </li>
                    ) : null}
                    {sub.items.map(item => {
                      const to = buildItemLink(item);

                      const isActive = currentModuleItemId != null && String(item.id) === currentModuleItemId;
                      const isCompleted = completedByItemId.has(String(item.id));
                      const isOptional = sub.required === false;

                      return (
                        <li
                          key={item.id}
                          ref={isActive ? activeItemRef : undefined}
                          className={`mt-1.5 overflow-hidden rounded-xl border border-gray bg-white transition-colors ${
                            isActive
                              ? "border-lac-leman bg-lac-leman-8/60"
                              : "hover:border-gray hover:bg-lac-leman-8/40"
                          } cursor-pointer md:cursor-default`}
                          data-active={isActive || undefined}>
                          <Link
                            to={to ? to : "#"}
                            aria-current={isActive ? "page" : undefined}
                            className={`flex items-center justify-between rounded-xl px-3 py-2.5 hover:bg-lac-leman-8/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lac-leman focus-visible:ring-offset-2 focus-visible:ring-offset-white ${isActive ? "bg-lac-leman-8/60" : "bg-white"}`}
                            aria-label={`${item.type}: ${item.title}`}
                            onClick={() => onNavigate?.()}>
                            <span className="min-w-0 flex-1 truncate text-sm font-medium md:text-sm">{item.title}</span>

                            <span
                              className={`ml-4 inline-flex h-4 min-w-4 items-center justify-center rounded-full border ${
                                isCompleted ? "border-lavaux bg-lavaux text-white" : "border-gray bg-white text-[0]"
                              } ${item.completion_requirement ? "" : "opacity-0"} ${
                                isProgressionSuccess ? "" : "opacity-0"
                              }`}>
                              {isCompleted ? <CheckIcon className="h-2.5 w-2.5" /> : null}
                            </span>
                            {isOptional ? (
                              <span className="text-gray-500 ml-auto inline-flex shrink-0 items-center rounded-full border border-gray px-2 py-1 text-[9px] font-semibold uppercase leading-none tracking-[0.15em]">
                                Optional
                              </span>
                            ) : null}
                          </Link>
                        </li>
                      );
                    })}
                  </React.Fragment>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default ModulesSidebar;
